import { ACTION_GROUPS, WORKFLOW_SUMMARY_LABELS } from "@wms/domain";
import type { ActionMeta, Workflow, WorkflowUnitTotal } from "@wms/domain";
import { qtyText } from "../portal/requestForm";

/**
 * ТЗ «Рабочие столы и персонализация» (31.10.2026), раздел 11 (UXW-47…53) и приложение §7: the pure part of the grouped
 * action panel of a document card. It only sorts the actions the card already has into places — the current step with
 * one main button, «Другие действия», later steps with their reasons, «Документы», «Этикетки», «ИИ-помощник» and the
 * dangerous ones apart. Nothing is added, renamed or dropped: every action keeps its code, kind, fields, permission,
 * preview, confirmation and handler (UXW-49), and the union of the groups is exactly the list the old row showed.
 * What is current and what is next comes from the server's workflow; without a recommendation nothing is guessed (UXW-48).
 */

/** Stable order of the buttons: by step group, then by the order inside the group, then as registered (UI-21). */
export function orderActions(actions: ActionMeta[]): ActionMeta[] {
  return actions
    .map((action, index) => ({ action, index }))
    .sort((a, b) => {
      const ga = ACTION_GROUPS[a.action.group ?? ""]?.order ?? 45;
      const gb = ACTION_GROUPS[b.action.group ?? ""]?.order ?? 45;
      return ga - gb || (a.action.order ?? 100) - (b.action.order ?? 100) || a.index - b.index;
    })
    .map((x) => x.action);
}

/** `when` of an action against the current row (the client flow of 30.10.2026: «present» — the field is filled). */
export function whenHolds(when: NonNullable<ActionMeta["when"]>, row: Record<string, unknown> | undefined): boolean {
  if (!row) return true;
  const value = row[when.field];
  if (when.present !== undefined) return when.present === !(value === null || value === undefined || value === "" || (typeof value === "object" && Object.keys(value as object).length === 0));
  return value === when.value;
}

/**
 * The actions a card shows for this status: the ones of the status (and their `when`), and — with a workflow — the
 * future steps of the path the server marks «disabled», in the stable order. The same list the row of HEAD showed.
 */
export function visibleActions(actions: ActionMeta[], status: string | undefined, row: Record<string, unknown> | undefined, workflow: Workflow | null | undefined): ActionMeta[] {
  const byStatus = actions.filter((a) => (!a.statuses || (status && a.statuses.includes(status))) && (!a.when || whenHolds(a.when, row)));
  if (!workflow) return byStatus;
  const future = actions.filter((a) => !byStatus.includes(a) && workflow.actions[a.code]?.state === "disabled");
  return orderActions([...byStatus, ...future]);
}

/** UXW-53: cancel / delete / write-off stand apart — by the meta (`danger`) and, as a safety net, by the stable code. */
const DANGER_CODES = /(^|[-_/])(cancel|delete|write-?off|annul)([-_/]|$)/;

export function isDanger(action: Pick<ActionMeta, "danger" | "code">): boolean {
  return !!action.danger || DANGER_CODES.test(action.code);
}

/** The words on a button: the server's subject label («Утвердить 7 товаров»), else the short label, else the name. */
export function buttonText(action: ActionMeta, workflow?: Workflow | null): string {
  return workflow?.actions[action.code]?.label ?? action.short_label ?? action.label;
}

/** The full name of the action: the title of its dialog, its tooltip and accessible name (UXW-05). */
export function fullName(action: ActionMeta, workflow?: Workflow | null): string {
  return workflow?.actions[action.code]?.label ?? action.label;
}

export interface PanelAction {
  action: ActionMeta;
  /** The words on the button (UXW-05). */
  text: string;
  /** The full name: tooltip, dialog title, the menu of documents. */
  full: string;
  /** Why it cannot be pressed now, in words; null — it can. */
  reason: string | null;
  /** UXW-50: «right» — the role lacks the right (a later step never unlocks it); «later» — a step or a condition. */
  blocked: "right" | "later" | null;
  recommended: boolean;
}

export type PrimaryItem =
  | { kind: "action"; item: PanelAction; consequence: string | null }
  /** UXW-48: «Добавить товары» — the next step is a tab of the card, not a disabled «Подтвердить». */
  | { kind: "tab"; key: string; label: string; consequence: string | null };

export interface CurrentStep {
  /** The server's words for the step («Подтвердить поступление», «Добавить товары», «Документ закрыт»). */
  title: string;
  instruction: string | null;
  tone: "action" | "tab" | "wait" | "done";
  primary: PrimaryItem | null;
  /** «Дальше: Подтверждение — сначала добавьте товары» from the stages of the server. */
  next: string | null;
}

export interface ActionGroups {
  /** null — the server gave no recommendation (or the card has no workflow): nothing is invented. */
  current: CurrentStep | null;
  other: PanelAction[];
  future: PanelAction[];
  documents: PanelAction[];
  labels: PanelAction[];
  assist: PanelAction[];
  danger: PanelAction[];
}

export interface GroupInput {
  actions: ActionMeta[];
  status?: string;
  row?: Record<string, unknown>;
  workflow?: Workflow | null;
  /** The right of the role for the action (the card's `can`). */
  allowed: (action: ActionMeta) => boolean;
  /** Why a button cannot be pressed — the same function the row of buttons uses (rights, assignee, workflow). */
  reasonOf: (action: ActionMeta) => string | null;
  /** The child tabs the card shows; a recommended tab the card does not show gets no button. */
  tabs?: string[];
}

/** «Дальше: …» — the stage after the live one, with its own words or the reason of its first action. */
export function nextWords(workflow: Workflow | null | undefined): string | null {
  if (!workflow) return null;
  const stages = workflow.stages;
  const live = stages.findIndex((s) => s.state === "current" || s.state === "waiting");
  if (live < 0) return null;
  const next = stages.slice(live + 1).find((s) => s.state !== "done" && s.state !== "skipped");
  if (!next) return null;
  if (next.state === "waiting") return `${next.label} — выполняет сотрудник с правом «${next.hint ?? "…"}»`;
  const reason = (next.actions ?? []).map((code) => workflow.actions[code]?.reason).find((r) => !!r);
  const tail = reason ?? next.hint ?? null;
  return tail ? `${next.label} — ${tail}` : next.label;
}

/**
 * The grouping itself. Every visible action lands in exactly one group (or is the main button of the current step);
 * the same function serves the stepwise card and the full card (UXW-47).
 */
export function groupActions(input: GroupInput): ActionGroups {
  const { workflow, allowed, reasonOf } = input;
  const visible = visibleActions(input.actions, input.status, input.row, workflow);
  const items = visible.map<PanelAction>((action) => {
    const reason = reasonOf(action);
    const state = workflow?.actions[action.code]?.state;
    const right = !allowed(action) || state === "forbidden";
    return {
      action,
      text: buttonText(action, workflow),
      full: fullName(action, workflow),
      reason,
      blocked: reason ? (right ? "right" : "later") : null,
      recommended: !!workflow?.actions[action.code]?.recommended,
    };
  });

  const rec = workflow?.recommended ?? null;
  let primary: PrimaryItem | null = null;
  if (rec?.kind === "action" && rec.code) {
    const item = items.find((i) => i.action.code === rec.code && !i.reason && !isDanger(i.action) && i.action.kind !== "print" && i.action.kind !== "labels");
    if (item) primary = { kind: "action", item, consequence: item.action.outcome ?? item.action.help ?? null };
  } else if (rec?.kind === "tab" && rec.key && (!input.tabs || input.tabs.includes(rec.key))) {
    primary = { kind: "tab", key: rec.key, label: rec.label, consequence: rec.instruction || null };
  }
  const current: CurrentStep | null = rec
    ? { title: rec.label, instruction: rec.instruction || null, tone: rec.kind, primary, next: rec.kind === "done" ? null : nextWords(workflow) }
    : null;

  const groups: ActionGroups = { current, other: [], future: [], documents: [], labels: [], assist: [], danger: [] };
  for (const item of items) {
    if (primary?.kind === "action" && primary.item === item) continue;
    const a = item.action;
    if (isDanger(a)) groups.danger.push(item);
    else if (a.kind === "print") groups.documents.push(item);
    else if (a.kind === "labels") groups.labels.push(item);
    else if (a.group === "assist") groups.assist.push(item);
    else if (item.reason) groups.future.push(item);
    else groups.other.push(item);
  }
  return groups;
}

/** Every action of the groups, the main one included — for the check «nothing is lost» (UXW-01, UXW-49). */
export function groupedActions(groups: ActionGroups): ActionMeta[] {
  const primary = groups.current?.primary?.kind === "action" ? [groups.current.primary.item.action] : [];
  const rest = [...groups.other, ...groups.future, ...groups.documents, ...groups.labels, ...groups.assist, ...groups.danger];
  return [...primary, ...rest.map((x) => x.action)];
}

// ---- totals per unit (UXW-47, UXW-51; appendix §7 п.10) -------------------------------------------------------------

/** The scalar total of the summary and the per-unit field it adds up. */
const UNIT_FIELDS: Record<string, string> = {
  plan_total: "quantity_plan",
  fact_total: "quantity_fact",
  picked_total: "quantity_picked",
  shipped_total: "quantity_shipped",
};

/** «160 шт; 2,5 кг» — every unit on its own, never «162,5». */
export function unitTotalsText(byUnit: WorkflowUnitTotal[], field: string): string | null {
  const parts = byUnit.filter((u) => u[field] !== undefined && u[field] !== null).map((u) => `${qtyText(u[field])} ${u.unit}`);
  return parts.length ? parts.join("; ") : null;
}

export interface SummaryRow {
  key: string;
  label: string;
  value: string;
}

/**
 * The rows of the summary of a step. The scalar `*_total` mean one unit only (CW31-B02): when the document mixes units
 * they are replaced by the sums per unit; `perUnit` asks for the units also when there is one («160 шт»).
 */
export function summaryRows(summary: Workflow["summary"] | null | undefined, perUnit = false): SummaryRow[] {
  if (!summary) return [];
  const byUnit = Array.isArray(summary.by_unit) ? (summary.by_unit as WorkflowUnitTotal[]) : [];
  const split = byUnit.length > 0 && (summary.mixed_units === true || perUnit);
  const rows: SummaryRow[] = [];
  for (const [key, value] of Object.entries(summary)) {
    const label = WORKFLOW_SUMMARY_LABELS[key];
    if (!label || value === null || value === undefined || value === "" || typeof value === "object" || typeof value === "boolean") continue;
    const field = UNIT_FIELDS[key];
    if (field && split) {
      const text = unitTotalsText(byUnit, field);
      if (text) rows.push({ key, label, value: text });
      continue;
    }
    rows.push({ key, label, value: String(value) });
  }
  return rows;
}
